import { useEffect, useRef, useState } from "react";
import type { Itinerary, ItineraryDay, ItineraryItem } from "@/lib/api/schemas";
import { itemImageURL, placesPhotoURL, streetViewURL } from "@/lib/placesPhoto";
import { cleanDescription } from "@/lib/cleanText";
import { dayColor } from "@/lib/dayColors";
import { useIsDesktop } from "@/lib/useIsDesktop";
import { PhotoLightbox } from "./PhotoLightbox";
import { DirectionsMenu } from "./DirectionsMenu";
import { DayDirectionsMenu } from "./DayDirectionsMenu";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ItineraryViewProps {
  itinerary: Itinerary;
  highlightedItemId?: string | null;
  activeDayNumber?: number | null;
  onSetActiveDay?: (dayNumber: number | null) => void;
  onItemFocus?: (itemId: string | null) => void;
  onItemSelect?: (itemId: string) => void;
  onItemSwap?: (item: ItineraryItem) => void;
  onReorder?: (itinerary: Itinerary) => void;
}

export function ItineraryView({
  itinerary,
  highlightedItemId,
  activeDayNumber,
  onSetActiveDay,
  onItemFocus,
  onItemSelect,
  onItemSwap,
  onReorder,
}: ItineraryViewProps) {
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  // Single-expansion policy across the whole itinerary: opening a new
  // card auto-closes any previously expanded one. Escape closes.
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  useEffect(() => {
    if (!expandedItemId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpandedItemId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedItemId]);

  // Auto-expand the focused day so its stops are always visible
  // when the user has filtered to it.
  useEffect(() => {
    if (!activeDayNumber) return;
    setCollapsed((prev) => ({ ...prev, [activeDayNumber]: false }));
  }, [activeDayNumber]);

  if (!itinerary?.days?.length) {
    return (
      <div className="text-center py-16 text-ink-light">
        <p className="text-lg mb-1 text-ink">No itinerary yet</p>
        <p className="text-sm">Chat with the AI to generate your trip plan</p>
      </div>
    );
  }

  const toggleDay = (dayNumber: number) =>
    setCollapsed((prev) => ({ ...prev, [dayNumber]: !prev[dayNumber] }));

  const allCollapsed = itinerary.days.every((d) => collapsed[d.dayNumber]);
  const setAll = (value: boolean) =>
    setCollapsed(
      Object.fromEntries(itinerary.days.map((d) => [d.dayNumber, value]))
    );

  const handleReorderDay = (dayNumber: number, newItems: ItineraryItem[]) => {
    if (!onReorder) return;
    onReorder({
      ...itinerary,
      days: itinerary.days.map((d) =>
        d.dayNumber === dayNumber ? { ...d, items: newItems } : d
      ),
    });
  };

  const handleToggleExpand = (itemId: string) => {
    setExpandedItemId((prev) => {
      const next = prev === itemId ? null : itemId;
      // Select on expand so the map marker lights up and re-centers.
      if (next) onItemSelect?.(itemId);
      return next;
    });
  };

  const activeDay = activeDayNumber
    ? itinerary.days.find((d) => d.dayNumber === activeDayNumber)
    : null;

  return (
    <div className="space-y-3">
      {/* Filter status bar: shown only while a day is active. Tapping
          "All days" clears the filter so the map re-expands to show
          every marker. */}
      {activeDayNumber && onSetActiveDay && (
        <div className="flex items-center gap-2 text-xs">
          <button
            onClick={() => onSetActiveDay(null)}
            className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-accent-light font-semibold hover:bg-accent/20 transition-colors"
          >
            <span aria-hidden="true">←</span>
            <span>All days</span>
          </button>
          {activeDay && (
            <span className="text-ink-light truncate min-w-0">
              Viewing <span className="text-ink font-semibold">{activeDay.title}</span>
              {activeDay.date && (
                <> · <span className="text-ink">{activeDay.date}</span></>
              )}
            </span>
          )}
        </div>
      )}

      {itinerary.days.length > 1 && !activeDayNumber && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <button
            onClick={() => setAll(!allCollapsed)}
            className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-ink-light hover:text-ink hover:bg-white/10 transition-colors"
          >
            {allCollapsed ? "Expand all" : "Collapse all"}
          </button>
        </div>
      )}

      {itinerary.days
        .filter((d) => !activeDayNumber || d.dayNumber === activeDayNumber)
        .map((day) => (
        <DayCard
          key={day.dayNumber}
          day={day}
          collapsed={!!collapsed[day.dayNumber]}
          onToggle={() => toggleDay(day.dayNumber)}
          isActive={activeDayNumber === day.dayNumber}
          isDimmed={!!activeDayNumber && activeDayNumber !== day.dayNumber}
          onSetActive={
            onSetActiveDay
              ? () =>
                  onSetActiveDay(
                    activeDayNumber === day.dayNumber ? null : day.dayNumber
                  )
              : undefined
          }
          highlightedItemId={highlightedItemId}
          expandedItemId={expandedItemId}
          onItemFocus={onItemFocus}
          onItemSelect={onItemSelect}
          onItemSwap={onItemSwap}
          onToggleExpand={handleToggleExpand}
          onReorder={
            onReorder
              ? (items) => handleReorderDay(day.dayNumber, items)
              : undefined
          }
        />
      ))}
    </div>
  );
}

function DayCard({
  day,
  collapsed,
  onToggle,
  isActive,
  isDimmed,
  onSetActive,
  highlightedItemId,
  expandedItemId,
  onItemFocus,
  onItemSelect,
  onItemSwap,
  onToggleExpand,
  onReorder,
}: {
  day: ItineraryDay;
  collapsed: boolean;
  onToggle: () => void;
  isActive?: boolean;
  isDimmed?: boolean;
  onSetActive?: () => void;
  highlightedItemId?: string | null;
  expandedItemId: string | null;
  onItemFocus?: (itemId: string | null) => void;
  onItemSelect?: (itemId: string) => void;
  onItemSwap?: (item: ItineraryItem) => void;
  onToggleExpand: (itemId: string) => void;
  onReorder?: (items: ItineraryItem[]) => void;
}) {
  const itemCount = day.items?.length || 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;
    const oldIndex = day.items.findIndex((i) => i.id === active.id);
    const newIndex = day.items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(day.items, oldIndex, newIndex));
  };

  const dayStripColor = dayColor(day.dayNumber);
  const handleHeaderClick = () => {
    // If this isn't the active day yet, make it active and expand it.
    // If it already is active, just toggle collapse as before.
    if (onSetActive && !isActive) {
      onSetActive();
    } else {
      onToggle();
    }
  };

  return (
    <div
      className={`frost-card rounded-xl overflow-hidden relative transition-all duration-200 ${
        isActive
          ? "ring-2 ring-accent/30 shadow-lg shadow-black/30"
          : isDimmed
            ? "opacity-60"
            : ""
      }`}
    >
      {/* Day-color strip running the full left edge of the card —
          ties every stop inside visually to the matching markers
          on the map (same color palette). */}
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: dayStripColor }}
      />
      {/* Header is a div + clickable area so we can host another
          interactive control (DayDirectionsMenu) alongside the
          collapse toggle without nesting buttons. */}
      <div className="relative border-b border-white/15 bg-white/5 hover:bg-white/10 transition-colors flex items-start gap-3 pl-5 pr-3 py-3">
        <button
          type="button"
          onClick={handleHeaderClick}
          className="absolute inset-0 w-full h-full"
          aria-expanded={!collapsed}
          aria-pressed={isActive}
          aria-label={`${day.title} — toggle`}
        />
        <span
          className={`text-ink-light pt-0.5 transition-transform duration-200 shrink-0 pointer-events-none ${
            collapsed ? "" : "rotate-90"
          }`}
          aria-hidden="true"
        >
          ▶
        </span>
        <div className="flex-1 min-w-0 pointer-events-none">
          {day.date && (
            <span
              className="inline-block text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md mb-1.5 text-white"
              style={{ backgroundColor: dayStripColor }}
            >
              {day.date}
            </span>
          )}
          <span className="flex items-center gap-2">
            <span className="font-semibold text-ink truncate min-w-0">
              {day.title}
            </span>
            <span className="text-xs text-ink-light shrink-0 whitespace-nowrap">
              {itemCount} {itemCount === 1 ? "stop" : "stops"}
            </span>
          </span>
          {day.summary && !collapsed && (
            <span className="block text-sm text-ink-light mt-1">
              {cleanDescription(day.summary)}
            </span>
          )}
        </div>
        {/* Directions menu sits in the top-right and intercepts its
            own clicks via stopPropagation so the header toggle
            doesn't fire under it. relative+z-10 keeps it above the
            absolute toggle overlay. */}
        <div className="relative z-10 self-start mt-0.5">
          <DayDirectionsMenu day={day} />
        </div>
      </div>
      {!collapsed && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={day.items.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="p-2 space-y-2">
              {(() => {
                // Per-day stop counter — only advance for items that
                // actually render a marker on the map (have coords).
                // Mirrors ItineraryMap's numbering so card badges and
                // marker labels always match.
                let stopIdx = 0;
                return day.items.map((item) => {
                  const hasCoords =
                    typeof item.lat === "number" &&
                    typeof item.lng === "number" &&
                    !(item.lat === 0 && item.lng === 0);
                  const stopNumber = hasCoords ? ++stopIdx : undefined;
                  return (
                    <ItemCard
                      key={item.id}
                      item={item}
                      stopNumber={stopNumber}
                      dayColor={dayStripColor}
                      highlighted={highlightedItemId === item.id}
                      expanded={expandedItemId === item.id}
                      onFocus={onItemFocus}
                      onSwap={onItemSwap}
                      onToggleExpand={onToggleExpand}
                      draggable={!!onReorder}
                    />
                  );
                });
              })()}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function ItemCard({
  item,
  stopNumber,
  dayColor,
  highlighted,
  expanded,
  onFocus,
  onSwap,
  onToggleExpand,
  draggable,
}: {
  item: ItineraryItem;
  stopNumber?: number;
  dayColor: string;
  highlighted?: boolean;
  expanded: boolean;
  onFocus?: (itemId: string | null) => void;
  onSwap?: (item: ItineraryItem) => void;
  onToggleExpand: (itemId: string) => void;
  draggable?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !draggable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const typeColors: Record<string, string> = {
    activity: "bg-accent/20 text-accent-light",
    meal: "bg-warm/20 text-white",
    transport: "bg-violet-400/20 text-violet-100",
    flight: "bg-cyan-300/20 text-cyan-100",
    hotel: "bg-emerald-300/20 text-emerald-100",
    free_time: "bg-white/15 text-ink-light",
  };

  const hasCoords =
    typeof item.lat === "number" &&
    typeof item.lng === "number" &&
    !(item.lat === 0 && item.lng === 0);
  const hasRichDetails =
    (item.photoNames && item.photoNames.length > 0) ||
    !!item.address ||
    !!item.rating ||
    !!item.website ||
    !!item.phone ||
    !!item.notes;
  const canExpand = hasRichDetails || hasCoords;

  const thumbnailURL = itemImageURL(item, { size: 320 });
  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  // Scroll the expanded card content into view when it opens.
  const expandedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!expanded || !expandedRef.current) return;
    const t = window.setTimeout(() => {
      expandedRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 80);
    return () => window.clearTimeout(t);
  }, [expanded]);

  const handleCardClick = () => {
    if (!canExpand) return;
    onToggleExpand(item.id);
  };
  const handleMouseEnter = () => {
    if (!hasCoords) return;
    onFocus?.(item.id);
  };
  const handleMouseLeave = () => {
    onFocus?.(null);
  };
  const openSwap = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSwap) onSwap(item);
  };
  // Only offer swap on real place-based items.
  const canSwap =
    !!onSwap &&
    item.type !== "flight" &&
    item.type !== "transport" &&
    item.type !== "free_time";

  const description = cleanDescription(item.description);

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role={canExpand ? "button" : undefined}
      tabIndex={canExpand ? 0 : undefined}
      onKeyDown={(e) => {
        if (canExpand && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onToggleExpand(item.id);
        }
      }}
      aria-expanded={canExpand ? expanded : undefined}
      id={`itinerary-item-${item.id}`}
      className={`relative rounded-xl overflow-hidden border transition-all duration-200 ${
        canExpand ? "cursor-pointer" : ""
      } ${
        highlighted
          ? "border-accent bg-accent/12 shadow-lg shadow-accent/25 scale-[1.01]"
          : expanded
            ? "border-accent/40 bg-white/[0.05]"
            : "border-white/10 bg-white/[0.025] hover:bg-white/[0.05] hover:border-white/20"
      } ${isDragging ? "opacity-60 z-10 shadow-2xl" : ""}`}
    >
      {/* Left accent bar — strong visual anchor for the selected state. */}
      {highlighted && (
        <div
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-accent-light to-accent rounded-r"
        />
      )}
      <div className="flex gap-3 p-3">
        {draggable && (
          <button
            type="button"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 w-5 self-stretch flex items-center justify-center text-ink-light/60 hover:text-ink cursor-grab active:cursor-grabbing touch-none -ml-1"
          >
            <span aria-hidden="true" className="text-lg leading-none select-none">
              ⋮⋮
            </span>
          </button>
        )}

        {/* Hero photo — Places photo preferred, Street View fallback.
            Quietly vanishes if the proxy returns 404 for this place.
            When a stop number is assigned (item has coords), overlay
            a colored numbered badge matching the map marker. */}
        {thumbnailURL && !thumbnailFailed ? (
          <div className="shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-white/10 bg-white/5 relative">
            <img
              src={thumbnailURL}
              alt=""
              loading="lazy"
              onError={() => setThumbnailFailed(true)}
              className="w-full h-full object-cover"
            />
            {stopNumber !== undefined && (
              <span
                aria-hidden="true"
                className="absolute top-1 left-1 w-6 h-6 rounded-full text-white text-[11px] font-bold flex items-center justify-center border-2 border-white shadow-md"
                style={{ backgroundColor: dayColor }}
              >
                {stopNumber}
              </span>
            )}
          </div>
        ) : stopNumber !== undefined ? (
          // No hero photo — fall back to an inline numbered badge so
          // the card still carries its map reference.
          <span
            aria-hidden="true"
            className="shrink-0 w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center border-2 border-white shadow-md self-start"
            style={{ backgroundColor: dayColor }}
          >
            {stopNumber}
          </span>
        ) : null}

        <div className="flex-1 min-w-0">
          {/* Header row: type pill + time + rating */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span
              className={`px-1.5 py-0.5 rounded font-medium ${
                typeColors[item.type] || "bg-gray-100 text-gray-600"
              }`}
            >
              {item.type.replace(/_/g, " ")}
            </span>
            {item.time && (
              <span className="font-semibold text-accent-light">
                {item.time}
              </span>
            )}
            {item.duration && (
              <span className="text-ink-light">· {item.duration}</span>
            )}
            {item.rating && item.rating > 0 ? (
              <span className="text-warm flex items-center gap-0.5 ml-auto">
                <span aria-hidden="true">★</span>
                <span>{item.rating.toFixed(1)}</span>
              </span>
            ) : null}
          </div>

          {/* Title */}
          <h3
            className={`mt-1 text-sm font-semibold leading-snug ${
              highlighted ? "text-white" : "text-ink"
            }`}
          >
            {item.title}
          </h3>

          {/* Subtitle: venue name (if distinct from title) */}
          {item.placeName && item.placeName !== item.title && (
            <p className="text-xs text-ink-light mt-0.5 truncate">
              {item.placeName}
            </p>
          )}

          {/* Description as a flowing paragraph */}
          {description && (
            <p
              className={`mt-1.5 text-sm leading-relaxed ${
                highlighted ? "text-ink" : "text-ink-light"
              }`}
            >
              {description}
            </p>
          )}

          {/* Footer meta + actions */}
          <div className="flex items-center gap-3 mt-2 flex-wrap text-xs">
            {item.cost && (
              <span className="text-ink-light">{item.cost}</span>
            )}
            {canSwap && (
              <button
                type="button"
                onClick={openSwap}
                className="text-ink-light hover:text-accent-light hover:underline"
              >
                ↺ Swap
              </button>
            )}
            {item.bookingUrl && (
              <a
                href={item.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-accent-light hover:text-accent hover:underline"
              >
                Book →
              </a>
            )}
            {canExpand && (
              <span
                aria-hidden="true"
                className={`ml-auto text-ink-light/70 transition-transform duration-200 ${
                  expanded ? "rotate-180" : ""
                }`}
              >
                ▾
              </span>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div ref={expandedRef}>
          <ExpandedDetails item={item} />
        </div>
      )}
    </div>
  );
}

function ExpandedDetails({ item }: { item: ItineraryItem }) {
  const placesPhotos = item.photoNames || [];
  const externalPhotos = item.photoUrls || [];
  // Carousel draws from unified list: Places first (proxied), then
  // Wikimedia / other direct URLs. Street View is a single-image
  // fallback when neither source has anything.
  const carousel = [
    ...placesPhotos
      .map((p) => placesPhotoURL(p, { maxWidth: 1200 }))
      .filter((u): u is string => !!u),
    ...externalPhotos,
  ];
  const [photoIdx, setPhotoIdx] = useState(0);
  const [heroFailed, setHeroFailed] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    setPhotoIdx(0);
    setHeroFailed(false);
    setLightboxIdx(null);
  }, [item.id]);

  const currentPhoto =
    carousel.length > 0
      ? carousel[photoIdx]
      : streetViewURL(item.lat, item.lng, { width: 640, height: 360 });

  // Higher-res list for the lightbox. Places photos are re-requested
  // at maxWidth=1600 for crisper viewing; direct URLs pass through.
  const lightboxPhotos =
    carousel.length > 0
      ? [
          ...placesPhotos
            .map((p) => placesPhotoURL(p, { maxWidth: 1600 }))
            .filter((u): u is string => !!u),
          ...externalPhotos,
        ]
      : currentPhoto
        ? [currentPhoto]
        : [];

  const hasCoords = !!(item.lat && item.lng);
  const destParam = hasCoords
    ? `${item.lat},${item.lng}`
    : item.placeName || item.title;
  // "View on map" — pins the place on Google Maps without triggering
  // a directions request.
  const googleMapsUrl = destParam
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        destParam
      )}`
    : undefined;

  const notes = cleanDescription(item.notes);

  return (
    <div
      className="border-t border-white/10 px-3 py-3 space-y-3"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Hero photo carousel. Omit entirely if none exists.
          Mobile + multiple photos: tapping advances to the next photo
          (Instagram-style). The "N photos" chip opens the full-screen
          lightbox on any device.
          Desktop: tapping the image opens the lightbox directly. */}
      {currentPhoto && !heroFailed && (
        <div className="relative rounded-lg overflow-hidden group">
          <button
            type="button"
            onClick={() => {
              if (!isDesktop && carousel.length > 1) {
                setPhotoIdx((i) => (i + 1) % carousel.length);
              } else {
                setLightboxIdx(photoIdx);
              }
            }}
            aria-label={
              !isDesktop && carousel.length > 1
                ? "Next photo"
                : "View photos full screen"
            }
            className="block w-full"
          >
            <img
              src={currentPhoto}
              alt={item.title}
              className="w-full h-44 md:h-56 object-cover transition-transform group-hover:scale-[1.015]"
              loading="lazy"
              onError={() => setHeroFailed(true)}
            />
          </button>
          {lightboxPhotos.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIdx(photoIdx);
              }}
              aria-label="View photos full screen"
              className="absolute top-2 right-2 px-2.5 py-1 rounded-full bg-black/70 text-white text-[10px] font-semibold border border-white/25 backdrop-blur-sm hover:bg-black/85 transition-colors inline-flex items-center gap-1"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" aria-hidden="true">
                <path d="M3 3h7v7M21 21h-7v-7M21 3l-8 8M3 21l8-8" />
              </svg>
              <span>{lightboxPhotos.length} photos</span>
            </button>
          )}
          {carousel.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPhotoIdx(
                    (i) => (i - 1 + carousel.length) % carousel.length
                  );
                }}
                aria-label="Previous photo"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-deep/70 border border-white/20 text-ink flex items-center justify-center hover:bg-deep/90"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPhotoIdx((i) => (i + 1) % carousel.length);
                }}
                aria-label="Next photo"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-deep/70 border border-white/20 text-ink flex items-center justify-center hover:bg-deep/90"
              >
                ›
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {carousel.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === photoIdx ? "w-5 bg-accent" : "w-1.5 bg-white/60"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {item.reviewCount && item.reviewCount > 0 ? (
        <p className="text-xs text-ink-light">
          {item.reviewCount.toLocaleString()} reviews on Google
        </p>
      ) : null}

      {/* Facts — only show sections that have content */}
      <div className="space-y-1.5 text-sm">
        {item.address && (
          <Row label="Address">
            <span className="text-ink">{item.address}</span>
          </Row>
        )}
        {item.phone && (
          <Row label="Phone">
            <a
              href={`tel:${item.phone.replace(/\s/g, "")}`}
              className="text-accent-light hover:text-accent"
            >
              {item.phone}
            </a>
          </Row>
        )}
        {item.website && (
          <Row label="Website">
            <a
              href={item.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-light hover:text-accent break-all"
            >
              {hostnameOf(item.website)}
            </a>
          </Row>
        )}
        {notes && (
          <Row label="Notes">
            <span className="text-ink-light">{notes}</span>
          </Row>
        )}
      </div>

      {/* Actions */}
      {(destParam || googleMapsUrl || item.bookingUrl) && (
        <div className="flex flex-wrap gap-2 pt-1">
          <DirectionsMenu
            lat={hasCoords ? item.lat : undefined}
            lng={hasCoords ? item.lng : undefined}
            placeName={item.placeName}
            placeId={item.placeId}
            title={item.title}
          />
          {googleMapsUrl && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 text-ink text-xs font-semibold hover:bg-white/15 transition-colors"
            >
              <span aria-hidden="true">📍</span>
              <span>View on map</span>
            </a>
          )}
          {item.bookingUrl && (
            <a
              href={item.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-electric/20 border border-electric/40 text-electric text-xs font-semibold hover:bg-electric/30 transition-colors"
            >
              Book now
            </a>
          )}
        </div>
      )}

      {lightboxIdx !== null && lightboxPhotos.length > 0 && (
        <PhotoLightbox
          photos={lightboxPhotos}
          startIndex={lightboxIdx}
          caption={item.placeName || item.title}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="w-20 shrink-0 text-[10px] uppercase tracking-wider text-ink-light/70 pt-0.5 font-semibold">
        {label}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}
